/**
 * Helpers for manipulating StructuredContent from the UI.
 */
import type { StructuredContent } from '@ojb/shared';

/**
 * Replace the first occurrence of `selectedText` with `replacement` anywhere
 * in the structured content. Walks the tree in rendered order (metadata →
 * attendees → action items → topics/bullets) and stops on the first match.
 *
 * Used by the RewriteBubble's onApply to commit Claude's rewrite back into
 * the source of truth. Edge case: same snippet appearing twice — we replace
 * only the first. Acceptable for the scaffold; could upgrade to a
 * selection-anchor tracker if it becomes a problem.
 *
 * Returns a new content object (immutable update). Returns the input
 * unchanged if no match is found.
 */
export function replaceFirstSnippet(
  content: StructuredContent,
  selectedText: string,
  replacement: string,
): StructuredContent {
  const needle = selectedText;
  if (!needle) return content;

  let done = false;
  const tryReplace = (field: string | undefined): string | undefined => {
    if (done || !field) return field;
    const idx = field.indexOf(needle);
    if (idx === -1) return field;
    done = true;
    return field.slice(0, idx) + replacement + field.slice(idx + needle.length);
  };

  const next: StructuredContent = { ...content };

  // Title / location / project
  next.title = tryReplace(next.title) ?? next.title;
  if (!done) next.location = tryReplace(next.location) ?? next.location;
  if (!done) next.project = tryReplace(next.project) ?? next.project;

  // Attendees
  if (!done) {
    next.attendeeGroups = next.attendeeGroups.map((group) => {
      if (done) return group;
      const firm = tryReplace(group.firm);
      if (firm !== group.firm) return { ...group, firm: firm! };
      // Members — join with '; ' and try replace, then split back.
      // `;` between members is the OJB convention; `,` is reserved
      // for credentials WITHIN a single member entry, e.g.
      // "Andrew Cridlin, ASLA". Splitting on `,` would shred
      // credentials into their own pills.
      const joined = group.members.join('; ');
      const replaced = tryReplace(joined);
      if (replaced !== joined && replaced !== undefined) {
        return {
          ...group,
          members: replaced.split(';').map((m) => m.trim()).filter(Boolean),
        };
      }
      return group;
    });
  }

  // Action items
  if (!done) {
    next.actionItems = next.actionItems.map((ai) => {
      if (done) return ai;
      const description = tryReplace(ai.description);
      if (description !== ai.description) return { ...ai, description: description! };
      if (ai.assigneeName) {
        const assignee = tryReplace(ai.assigneeName);
        if (assignee !== ai.assigneeName) return { ...ai, assigneeName: assignee };
      }
      return ai;
    });
  }

  // Topics + bullets
  if (!done) {
    next.topics = next.topics.map((topic) => {
      if (done) return topic;
      const title = tryReplace(topic.title);
      if (title !== topic.title) return { ...topic, title: title! };
      const bullets = topic.bullets.map((b) => {
        if (done) return b;
        const body = tryReplace(b.body);
        if (body !== b.body) return { ...b, body: body! };
        if (b.speaker) {
          const speaker = tryReplace(b.speaker);
          if (speaker !== b.speaker) return { ...b, speaker };
        }
        return b;
      });
      if (bullets.some((nb, i) => nb !== topic.bullets[i])) {
        return { ...topic, bullets };
      }
      return topic;
    });
  }

  return next;
}
